import { z } from 'zod'

import { BrandedMap, type ZodInfer } from '@mntn-dev/utility-types'

import { CreditUnitSchema } from '../billing/credit-unit.ts'
import { CurrencySchema } from '../billing/currency.ts'

const BaseCurrencySchema = z.enum(['DB']) // the unit used to store values in the database in US Cents

export const ExchangeUnitSchema = z.union([
  CreditUnitSchema,
  CurrencySchema,
  BaseCurrencySchema,
])

//  "DB" | "mntn_credits" | "meta" | "tiktok" | "linkedin" | "USD"

export type ExchangeUnit = ZodInfer<typeof ExchangeUnitSchema>

export const ExchangeRateSchema = z.partialRecord(
  ExchangeUnitSchema,
  z.number()
)

export type ExchangeRate = ZodInfer<typeof ExchangeRateSchema>

export type ExchangeRateMap = Record<string, number>
export const ExchangeRateMapSchema = z.custom<ExchangeRateMap>((data) => {
  return z.partialRecord(ExchangeUnitSchema, z.number()).safeParse(data).success
})

export const CentSchema = z.int().brand<'Cent'>()
export type Cent = ZodInfer<typeof CentSchema>
export const Cent = (value: number): Cent => CentSchema.parse(value)

export const DollarSchema = z.number().brand<'Dollar'>()
export type Dollar = ZodInfer<typeof DollarSchema>
export const Dollar = (value: number): Dollar => DollarSchema.parse(value)

export const MntnCreditSchema = z.number().brand<'MntnCredit'>()
export type MntnCredit = ZodInfer<typeof MntnCreditSchema>
export const MntnCredit = (value: number): MntnCredit =>
  MntnCreditSchema.parse(value)

export const MetaCreditSchema = z.number().brand<'MetaCredit'>()
export type MetaCredit = ZodInfer<typeof MetaCreditSchema>
export const MetaCredit = (value: number): MetaCredit =>
  MetaCreditSchema.parse(value)

export const TiktokCreditSchema = z.number().brand<'TiktokCredit'>()
export type TiktokCredit = ZodInfer<typeof TiktokCreditSchema>
export const TiktokCredit = (value: number): TiktokCredit =>
  TiktokCreditSchema.parse(value)

export const LinkedinCreditSchema = z.number().brand<'LinkedinCredit'>()
export type LinkedinCredit = ZodInfer<typeof LinkedinCreditSchema>
export const LinkedinCredit = (value: number): LinkedinCredit =>
  LinkedinCreditSchema.parse(value)

export const CreditSchema = z.union([
  MntnCreditSchema,
  MetaCreditSchema,
  TiktokCreditSchema,
  LinkedinCreditSchema,
])
export type Credit = ZodInfer<typeof CreditSchema>

export const ExchangeUnitSchemaMap = new BrandedMap<
  ExchangeUnit,
  z.ZodType<number>
>({
  DB: CentSchema,
  mntn_credits: MntnCreditSchema,
  meta: MetaCreditSchema,
  tiktok: TiktokCreditSchema,
  linkedin: LinkedinCreditSchema,
  USD: DollarSchema,
})

export const ExchangeValueSchema = z.union([
  CentSchema,
  MntnCreditSchema,
  MetaCreditSchema,
  TiktokCreditSchema,
  LinkedinCreditSchema,
  DollarSchema,
])

export type ExchangeValue = ZodInfer<typeof ExchangeValueSchema>

export type ToExchangeValue<T extends ExchangeUnit> = T extends 'DB'
  ? Cent
  : T extends 'USD'
    ? Dollar
    : T extends 'mntn_credits'
      ? MntnCredit
      : T extends 'meta'
        ? MetaCredit
        : T extends 'tiktok'
          ? TiktokCredit
          : T extends 'linkedin'
            ? LinkedinCredit
            : never

export function exchangeValueForUnit(unit: 'DB', value: number): Cent
export function exchangeValueForUnit(unit: 'USD', value: number): Dollar
export function exchangeValueForUnit(
  unit: 'mntn_credits',
  value: number
): MntnCredit
export function exchangeValueForUnit(unit: 'meta', value: number): MetaCredit
export function exchangeValueForUnit(
  unit: 'tiktok',
  value: number
): TiktokCredit
export function exchangeValueForUnit(
  unit: 'linkedin',
  value: number
): LinkedinCredit
export function exchangeValueForUnit<T extends ExchangeUnit>(
  unit: T,
  value: number
): ToExchangeValue<T>
export function exchangeValueForUnit(unit: string, value: number) {
  return ExchangeUnitSchemaMap.get(unit as ExchangeUnit).parse(value)
}
